import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse, HttpParams } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { Router, RouterStateSnapshot } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ModelMapper } from '../modelmapper';
import { UserPrincipal } from './userPrincipal.model';
import { SESSION_STORAGE, WebStorageService } from 'ngx-webstorage-service';
import { OauthResponse } from './oauthResponse.model';
import { Constants } from 'src/app/data-models/constants.enum';
import { JsonPipe } from '@angular/common';
import { AzureAuth } from './azureAuth.modal';

@Injectable({
    providedIn: 'root'
})
export class OAuthService {
    private readonly currentUserSubject: BehaviorSubject<UserPrincipal>;
    public currentUser: Observable<UserPrincipal>;
    private userPrincipal: UserPrincipal = new UserPrincipal();
    private azureAuth: AzureAuth = new AzureAuth();
    private readonly oauthResponse: OauthResponse = new OauthResponse();
    private baseUrl = "";
    private readonly pingfederateRedirectUrl = environment.pingfederateRedirectUrl;
    private readonly pingfederateLoginUrl = environment.pingfederateLoginUrl + this.pingfederateRedirectUrl;
    private readonly pingLogoutUrl = environment.logoutUrl;
    private refreshTokenSubscription: any;
    private azureTokenRefreshSubscription: any;

    private readonly contentTypeJson = 'application/json';


    constructor(private readonly http: HttpClient, private readonly router: Router, @Inject(SESSION_STORAGE) private readonly sessionStorage: WebStorageService) {
        console.log('auth service constructor is called');
        this.currentUserSubject = new BehaviorSubject<UserPrincipal>(this.userPrincipal);
        this.currentUser = this.currentUserSubject.asObservable();
    }


    redirectToPingfederateLogin(forceCredentialPrompt: boolean = false) {
        const shouldForcePrompt = forceCredentialPrompt || localStorage.getItem('forceCredentialPrompt') === 'true';
        window.location.href = this.getPingFederateLoginUrl(shouldForcePrompt);
    }

    private getPingFederateLoginUrl(forceCredentialPrompt: boolean): string {
        if (!forceCredentialPrompt) {
            return this.pingfederateLoginUrl;
        }
        return `${this.pingfederateLoginUrl}&prompt=login&max_age=0`;
    }

    async getUserAuthentication<T>(authCode: string) {
        // Check and refresh Azure token if needed, then ensure it's fetched and stored
        this.checkAndRefreshAzureToken();
        await this.fetchAndStoreAzureApiToken().toPromise();
        console.log('auth code from optumID is ----> ' + authCode);
        const authRequest = {
            oauthCode: authCode,
            redirectUri: this.pingfederateRedirectUrl,
        };
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': this.contentTypeJson,
                'Authorization': `Bearer ${sessionStorage.getItem('azureApiToken')}`
            })
        };
        const oauthURL = environment.accessTokenServerUrl + environment.oauthEndpoint;
        return this.http.post<OauthResponse>(oauthURL, authRequest, httpOptions).toPromise();
    }


    getUserPrincipalAppLvl() {
        const resp : OauthResponse = JSON.parse(sessionStorage.getItem("oAuthResponse"));
        let headers = new HttpHeaders(); // create header object
        console.log('stargate enabled flag in oauth reponse is '+resp.stargateEnabled);
        headers = headers.append('UserToken','Bearer '+resp.access_token)
        .append('Authorization', `Bearer ${sessionStorage.getItem('azureApiToken')}`);
        if(resp.stargateEnabled){
            this.baseUrl = environment.starGateUrl+environment.reportingServiceBaseUrlSuffix;
        } else {
            this.baseUrl = environment.backedBaseUrl+environment.reportingServiceBaseUrlSuffix;
        }
        const userEndPointURL = this.baseUrl + environment.userEndPoint;
        return this.http.get(userEndPointURL,  { headers: headers, observe: 'response' }).pipe(
            map((response: HttpResponse<any>) => response.body),
            catchError((error: HttpErrorResponse) => {
                this.logUserPrincipalFailure(error, userEndPointURL, resp);
                return throwError(error);
            })
        ).toPromise();
    }

	async processAuth(authCode: string){
        await this.getUserAuthentication(authCode).then(response => {
            this.setOAuthResponse(response);
        }, error => this.redirectToPingfederateLogin());
        const resp : OauthResponse = JSON.parse(sessionStorage.getItem("oAuthResponse"));
        console.log('*** auth response', resp);
        await this.getUserPrincipalAppLvl().then((response: any) => {
			console.log('*** app response',response);
			this.userPrincipal = new ModelMapper(UserPrincipal).map(response);
			this.userPrincipal.expires_in=resp.expires_in;
            this.userPrincipal.refreshToken=resp.refresh_token;
            this.userPrincipal.accessToken=resp.access_token;
            this.userPrincipal.tokenType=resp.token_type;
			this.setUserDataStorage(this.userPrincipal);
        }, error =>  {
            console.log('error while fetching user info', error);
            this.logUserPrincipalFailure(error, this.baseUrl + environment.userEndPoint, resp);
        }
        );
		return true;
	}

    private logUserPrincipalFailure(error: any, url: string, oauthResp: OauthResponse | null): void {
        const httpError = error as HttpErrorResponse;
        const tokenClaims = this.getTokenClaimsSafe(oauthResp?.access_token);
        const tokenSummary = {
            tokenType: oauthResp?.token_type,
            stargateEnabled: oauthResp?.stargateEnabled,
            aud: tokenClaims?.aud,
            scope: tokenClaims?.scope,
            client_id: tokenClaims?.client_id,
            sub: tokenClaims?.sub,
            iss: tokenClaims?.iss,
            exp: tokenClaims?.exp,
            iat: tokenClaims?.iat
        };

        console.group('[AuthDebug] userprincipals request failed');
        console.log('requestUrl', url);
        console.log('status', httpError?.status);
        console.log('statusText', httpError?.statusText);
        console.log('message', httpError?.message);
        console.log('responseBody', httpError?.error);
        console.log('responseHeaders', {
            x_correlation_id: httpError?.headers?.get('x-correlation-id'),
            x_request_id: httpError?.headers?.get('x-request-id'),
            content_type: httpError?.headers?.get('content-type')
        });
        console.log('tokenSummary', tokenSummary);
        console.groupEnd();
    }

    private getTokenClaimsSafe(token: string | null | undefined): { [key: string]: any } | null {
        if (!token || token.split('.').length < 2) {
            return null;
        }

        try {
            const payload = token.split('.')[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            const decoded = atob(payload);
            return JSON.parse(decoded);
        } catch (e) {
            console.log('[AuthDebug] failed to decode access token claims', e);
            return null;
        }
    }

    setUserDataStorage(userPrincipal: UserPrincipal): boolean {
        sessionStorage.setItem('userPrincipal', JSON.stringify(userPrincipal));
        sessionStorage.setItem('expDate', this.calculateTokenExpireDate().toString());
        localStorage.setItem('userPrincipal', JSON.stringify(userPrincipal));
        this.currentUserSubject.next(userPrincipal);
        return true;
    }

    setOAuthResponse(response): boolean{
        sessionStorage.setItem('oAuthResponse',JSON.stringify(response));
        return true;
    }

    private removeStorageItemIfPresent(storage: Storage, key: string) {
        if (storage.getItem(key) !== null) {
            storage.removeItem(key);
        }
    }

    clearUserDataStorage() {
        this.removeStorageItemIfPresent(sessionStorage, "userPrincipal");
        this.removeStorageItemIfPresent(sessionStorage, "expDate");
        this.removeStorageItemIfPresent(sessionStorage, "oAuthResponse");
        this.removeStorageItemIfPresent(sessionStorage, "accessToken");
        this.removeStorageItemIfPresent(sessionStorage, "refreshToken");
        this.removeStorageItemIfPresent(localStorage, "userPrincipal");
        this.removeStorageItemIfPresent(localStorage, "oAuthResponse");
        this.removeStorageItemIfPresent(localStorage, "accessToken");
        this.removeStorageItemIfPresent(localStorage, "refreshToken");
        this.userPrincipal = null;
        this.currentUserSubject.next(null);
    }

    sessionTimeoutRedirect(){
        localStorage.setItem('forceCredentialPrompt', 'true');
        this.auditSecureEvent("5");
        this.clearUserDataStorage();
    }

    logoutUser() {
       this.auditSecureEvent("2");
       this.clearUserDataStorage();
    }

    logoutUserRedirectSignIn(idTokenHint?: string) {
        const resp : OauthResponse = idTokenHint ? null : JSON.parse(sessionStorage.getItem("oAuthResponse"));
        const tokenHint = idTokenHint || resp?.id_token;
        if (!tokenHint) {
            window.location.href = this.pingfederateLoginUrl;
            return;
        }
        const logoutEndPoint : string = this.pingLogoutUrl + tokenHint + '&post_logout_redirect_uri=' + this.pingfederateRedirectUrl + '&state=userLogggedOut';
        window.location.href = logoutEndPoint;
     }

    private  baseServerUrl = environment.baseServerUrl;
    private getReportingBaseUrl() {
        const resp = this.getOauthResponse();
        if (!resp) {
            return '';
        }
        return resp.stargateEnabled ?
            environment.starGateUrl + environment.reportingServiceBaseUrlSuffix :
            environment.backedBaseUrl + environment.reportingServiceBaseUrlSuffix;
    }

    getBaseServerUrl(){
        const resp : OauthResponse = JSON.parse(sessionStorage.getItem("oAuthResponse"));
        if(resp.stargateEnabled){
            this.baseServerUrl = environment.starGateUrl+environment.reportingServiceBaseUrlSuffix;
        }
        else{
            this.baseServerUrl = environment.backedBaseUrl+environment.reportingServiceBaseUrlSuffix;
        }
      }

    auditSecureEvent(eventId:string){
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': this.contentTypeJson,
            })
        };
        const userDataRaw = sessionStorage.getItem("userPrincipal");
        if (!userDataRaw) {
            return;
        }
        const userData:UserPrincipal = JSON.parse(userDataRaw);
        if (!userData) {
            return;
        }
        const sessionIdGenerated:string = userData.sessionIdGenerated;
        const userId:string = userData.preferred_username;
        if (!sessionIdGenerated || !userId) {
            return;
        }
        let prtySk = null;
        const clientRaw = sessionStorage.getItem(Constants.clientName);
        if (clientRaw) {
            try {
                const client = JSON.parse(JSON.parse(clientRaw));
                prtySk = client && client.value;
            } catch (error) {
                console.log('Unable to parse clientName from session storage', error);
            }
        }
        if(eventId == "2")
        {
            this.getBaseServerUrl();
            if (!this.baseServerUrl) {
                return;
            }
            this.http.post<any[]>(`${this.baseServerUrl}/v1/logout` , {eventId,sessionIdGenerated,userId,prtySk} ,httpOptions).subscribe(response => {
                console.log('logout response', response);
                console.log('logout response', JSON.stringify(response));
            });
        }
        else
        {
            if (!this.baseUrl) {
                this.baseUrl = this.getReportingBaseUrl();
            }
            if (!this.baseUrl) {
                return;
            }
            this.http.post<any[]>(`${this.baseUrl}/auditSecurityEvent` , {eventId,sessionIdGenerated,userId} ,httpOptions).subscribe(response => {});
        }
    }

    getUserPrincipal(): UserPrincipal {
        const userPrincipal: string = sessionStorage.getItem('userPrincipal');
        return (userPrincipal === null || userPrincipal === '' || userPrincipal === undefined) ? null : JSON.parse(userPrincipal);
    }

    getOauthResponse(): OauthResponse {
        const resp: string = sessionStorage.getItem('oAuthResponse');
        return (resp === null || resp === '' || resp === undefined) ? null : JSON.parse(resp);
    }
    getAzureAuthResponse(): AzureAuth {
        const resp: string = sessionStorage.getItem('azureAuth');
        return (resp === null || resp === '' || resp === undefined) ? null : JSON.parse(resp);
    }
    isRefreshTokenTriggered(){
       return this.refreshTokenSubscription;
    }

    checkAndRefreshAccessToken() {
        const currentTime: number = (new Date()).getTime();
        const expireTime: number = (new Date(sessionStorage.getItem("expDate"))).getTime();
        const timeremaining: number = Math.abs((expireTime - currentTime) / 60000);
        console.log('****timeremaining',timeremaining,expireTime,currentTime);
        if (timeremaining < 20 &&  !this.refreshTokenSubscription) {
            this.refreshTokenSubscription=this.getRefreshedAccessToken()
                .subscribe((response) => {
                    console.log('refresh response ', response);
                    this.userPrincipal = this.getUserPrincipal();
                    this.userPrincipal.expires_in=response.expires_in;
                    this.userPrincipal.refreshToken=response.refresh_token;
                    this.userPrincipal.accessToken=response.access_token;
                    this.userPrincipal.tokenType=response.token_type;
                    this.setUserDataStorage(this.userPrincipal);
                    const oAuthResponse = this.getOauthResponse();
                    oAuthResponse.access_token = this.userPrincipal.accessToken;
                    oAuthResponse.refresh_token = this.userPrincipal.refreshToken;
                    oAuthResponse.expires_in = this.userPrincipal.expires_in;
                    oAuthResponse.id_token = response.id_token;
                    this.setOAuthResponse(oAuthResponse);
                    sessionStorage.setItem('expDate', this.calculateTokenExpireDate().toString());
                    this.refreshTokenSubscription=null;
                }, error => {
                    console.log('refresh error response', error);
                    this.refreshTokenSubscription=null;
                });
        }
    }

    getRefreshedAccessToken() {
        const userPrincipal: UserPrincipal = this.getUserPrincipal();
        const refreshRequest = {
            refresh_token: userPrincipal.refreshToken
        }
        const httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': this.contentTypeJson
            })
        };
        let refreshUrl = environment.backedBaseUrl+environment.reportingServiceBaseUrlSuffix;
        if(this.getOauthResponse().stargateEnabled){
            refreshUrl = environment.starGateUrl+environment.reportingServiceBaseUrlSuffix;
        }
        refreshUrl += environment.userEndPoint + 'refreshAccessToken';
        return this.http.post<OauthResponse>(refreshUrl, refreshRequest, httpOptions);
    }

    calculateTokenExpireDate(): Date {
        const expireDate = new Date();
        // set expired date to be 60 seconds before the real expired time to account for network issue etc
        const expiresIn = parseInt(this.userPrincipal.expires_in, 10);
        expireDate.setUTCSeconds(expireDate.getUTCSeconds() + (expiresIn - 60));
        return expireDate;
    }

    isUserSessionValid() {
        const userPrincipal: UserPrincipal = JSON.parse(sessionStorage.getItem('userPrincipal'));
        const expDate: Date = sessionStorage.getItem('expDate') == null ? null : new Date(sessionStorage.getItem('expDate'));
        const currentDate: Date = new Date();
        setInterval(this.checkAndRefreshAccessToken.bind(this), 10000);
        setInterval(this.checkAndRefreshAzureToken.bind(this), 10000);
        if (userPrincipal && userPrincipal.accessToken != null && userPrincipal.refreshToken != null && expDate != null && expDate.getTime() >= currentDate.getTime()) {
            const currentUsrPrnc = this.currentUserSubject.getValue();
            if (currentUsrPrnc && !currentUsrPrnc.email) {
                this.currentUserSubject.next(userPrincipal);
            }
            return true;
        } else {
            return false;
        }
    }

    isTermsAccepeted(){
        const userPrincipal: UserPrincipal = JSON.parse(sessionStorage.getItem('userPrincipal'));
        return userPrincipal && userPrincipal.isTNCAccepted === "Y";
    }

    getSupportEmail(emailType){
        const resp : OauthResponse = JSON.parse(sessionStorage.getItem("oAuthResponse"));
        let baseUrl = environment.backedBaseUrl+environment.maintenanceServiceBaseUrlSuffix;
        if(resp.stargateEnabled){
            baseUrl = environment.starGateUrl+environment.maintenanceServiceBaseUrlSuffix;
        }
        return this.http.post(`${baseUrl}/presentation/authorization/supportEmail-details`,{typeOfMailId: emailType});
      }

      /**
     * Gets an OAuth token from the new Stargate API endpoint.
     * @returns An Observable with the token response.
     */
    fetchAndStoreAzureApiToken(): Observable<any> {
        const azureApiClientId = environment.azureApiClientId;
        const azureApiClientSecret = environment.azureApiClientSecret;

        if (!azureApiClientId || !azureApiClientSecret) {
            return throwError(() => new Error('Missing Azure API credentials in environment configuration'));
        }

        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'Accept': 'application/json'
        });

        const body = new HttpParams()
            .set('grant_type', 'client_credentials')
            .set('client_id', azureApiClientId)
            .set('client_secret', azureApiClientSecret)
            .set('scope', 'https://api.uhg.com/.default');

        return this.http.post(environment.stargatetokenUrl, body.toString(), { headers }).pipe(
            tap(tokenResponse => {
                // Store the new token in session storage
                sessionStorage.setItem('azureApiToken', (tokenResponse as any).access_token);
                sessionStorage.setItem('azureExpireIn', (tokenResponse as any).expires_in);
                this.azureAuth = new ModelMapper(AzureAuth).map(tokenResponse);
                sessionStorage.setItem('azureAuth', JSON.stringify(this.azureAuth));
                // Store the calculated expiration date
                sessionStorage.setItem('azureTokenExpireDate', this.calculateAzureTokenExpireDate().toString());
                console.log('Azure API token fetched and stored successfully');
            })
        );
    }

    calculateAzureTokenExpireDate(): Date {
        const expireDate = new Date();
        // set expired date to be 60 seconds before the real expired time to account for network issue etc
        const azureExpireInStr = sessionStorage.getItem('azureExpireIn');

        if (!azureExpireInStr) {
            // Fallback to azureAuth object if azureExpireIn is not available
            try {
                const azureAuth: AzureAuth = JSON.parse(sessionStorage.getItem('azureAuth'));
                if (azureAuth && azureAuth.expires_in) {
                    expireDate.setUTCSeconds(expireDate.getUTCSeconds() + (azureAuth.expires_in - 60));
                    return expireDate;
                }
            } catch (e) {
                console.log('[AuthDebug] Failed to parse azureAuth from session storage', e);
            }
            return expireDate; // Return current date if unable to calculate
        }

        const azureExpireIn = parseInt(azureExpireInStr, 10);
        expireDate.setUTCSeconds(expireDate.getUTCSeconds() + (azureExpireIn - 60));
        return expireDate;
    }

    /**
     * Checks if Azure token is expired or about to expire and refreshes it if needed.
     * Prevents concurrent refresh requests using azureTokenRefreshSubscription.
     */
    checkAndRefreshAzureToken(): void {
        const currentTime: number = new Date().getTime();
        const azureExpireDateStr = sessionStorage.getItem('azureTokenExpireDate');

        if (!azureExpireDateStr) {
            // Token not yet fetched, will be fetched by fetchAndStoreAzureApiToken
            console.log('[AuthDebug] Azure token expiration date not found in session storage');
            return;
        }

        const expireTime: number = new Date(azureExpireDateStr).getTime();
        const timeRemaining: number = (expireTime - currentTime) / 1000; // in seconds

        console.log('Azure token time remaining (seconds):', timeRemaining);

        if (timeRemaining < 60 && !this.azureTokenRefreshSubscription) {
            // Refresh token if less than 60 seconds remaining and no refresh is already in progress
            console.log('[AuthDebug] Azure token expiring soon, initiating refresh');
            this.azureTokenRefreshSubscription = this.fetchAndStoreAzureApiToken()
                .subscribe(
                    (response) => {
                        console.log('[AuthDebug] Azure token refreshed successfully');
                        this.azureTokenRefreshSubscription = null;
                    },
                    (error) => {
                        console.error('[AuthDebug] Azure token refresh failed', error);
                        this.azureTokenRefreshSubscription = null;
                    }
                );
        }
    }

    /**
     * Checks if Azure token is valid and not expired.
     * @returns true if token exists and is not expired, false otherwise
     */
    isAzureTokenValid(): boolean {
        const azureToken = sessionStorage.getItem('azureApiToken');
        const azureExpireDateStr = sessionStorage.getItem('azureTokenExpireDate');

        if (!azureToken || !azureExpireDateStr) {
            return false;
        }

        const expireTime: number = new Date(azureExpireDateStr).getTime();
        const currentTime: number = new Date().getTime();

        return currentTime < expireTime;
    }

    /**
     * Checks if Azure token is valid before use, refreshes if needed.
     * @returns Promise that resolves with the valid Azure token
     */
    async getValidAzureToken(): Promise<string> {
        this.checkAndRefreshAzureToken();

        // If token is not valid, fetch a new one
        if (!this.isAzureTokenValid()) {
            await this.fetchAndStoreAzureApiToken().toPromise();
        }

        return sessionStorage.getItem('azureApiToken');
    }
}
